/* eslint-disable react/self-closing-comp */
import { FunctionComponent, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router';
import {
  Button,
  ButtonContainer,
  ContentSection,
  Heading,
  Layout,
  Paragraph,
  Table,
  Tag,
} from '@vwfs-bronson/bronson-react';
import { currencySymbol } from '../../../../../config';
import { QuoteFlow } from '../../../../../resources/base/models/enums';
import { getQuote } from '../../../../../services/api/miles/miles-services';
import { store } from '../../../../../services/redux';
import { setIsCarUsed, setSelectedVehicleType } from '../../../../../services/redux/features/custom.slice';
import { setCustomerInitialState } from '../../../../../services/redux/features/customer.slice';
import {
  dashboardSelectors,
  setSelectedComparisonOfferIds,
} from '../../../../../services/redux/features/dashboard.slice';
import { loadingActions } from '../../../../../services/redux/features/loading.slice';
import { setQuoteData, setQuoteFlow } from '../../../../../services/redux/features/quote.slice';
import { getInitialCustomerStateFromQuote } from '../../../../../services/redux/reduxUtils';
import { useNavigate } from '../../../../../services/routing/navigate';
import { additionalProductsTranslationsMap, getVehicleType, isCarUsedFromQuote } from '../../../../utils';

const extractOfferId = (quote: QuoteSchema.Schemas.QuoteResponse) => {
  return quote.financialProduct?.offers?.[0].offerId;
};

const extractOfferReference = (quote: QuoteSchema.Schemas.QuoteResponse) => {
  return quote.financialProduct?.offers?.[0].offerReference;
};

const extractAdditionalProducts = (quote: QuoteSchema.Schemas.QuoteResponse) => {
  return quote.financialProduct?.offers?.[0].additionalProducts
    ?.filter((product) => {
      return product.choices?.some((choice) => choice.isSelected && choice.name?.toLowerCase() !== 'not included');
    })
    .map((product) => product.name)
    .map((name) => {
      return (
        <Tag key={name} className="u-mr-xxsmall u-mb-xxsmall">
          {additionalProductsTranslationsMap[name || ''] || name}
        </Tag>
      );
    });
};

const extractFirstIncreasedPayment = (quote: QuoteSchema.Schemas.QuoteResponse) => {
  const grossAmount = quote.financialProduct?.offers?.[0].calculation?.firstInstallmentAmount?.grossAmount || 0;
  const choicesSum = quote.financialProduct?.offers?.[0].additionalProducts
    ?.map((product) => product.choices)
    .reduce((acc, item) => {
      const sum =
        item
          ?.filter((choice) => choice.id !== '100347')
          .reduce((a, elem) => a + (elem.choiceCostAmount?.amount || 0), 0) || 0;

      return acc + sum;
    }, 0);

  return (grossAmount + (choicesSum || 0)).toFixed(2) + ' ' + currencySymbol;
};

const extractEcologicalTaxes = (quote: QuoteSchema.Schemas.QuoteResponse, t) => {
  const components = quote.financialProduct?.offers?.[0].benefitPackages?.[0].components?.filter((component) =>
    ['400002', '400004'].includes(component.milesComponentTypeId || '')
  );

  if (components?.length !== 2) return t('comparisonPage:notApplicable');

  return (
    components
      .map((component) => component.alternativePeriodAmount || 0)
      .reduce((acc, item) => acc + item)
      .toFixed(2) +
    ' ' +
    currencySymbol
  );
};

const extractNonDeductibleAnnualDepreciation = (quote: QuoteSchema.Schemas.QuoteResponse, t) => {
  const components = quote.financialProduct?.offers?.[0].benefitPackages?.[0].components?.filter((component) =>
    ['250007'].includes(component.milesComponentTypeId || '')
  );

  if (components?.length !== 1) return t('comparisonPage:notApplicable');

  return (
    components
      .map((component) => component.alternativePeriodAmount || 0)
      .reduce((acc, item) => acc + item)
      .toFixed(2) +
    ' ' +
    currencySymbol
  );
};

const extractTotalMonthlyCost = (quote: QuoteSchema.Schemas.QuoteResponse, t) => {
  const benefitSum =
    quote.financialProduct?.offers?.[0].benefitPackages?.[0].components
      ?.filter((component) => ['250007'].includes(component.milesComponentTypeId || ''))
      .map((component) => component.alternativePeriodAmount || 0)
      .reduce((acc, item) => acc + item) || 0;

  return (
    // text color taken from bronson palette
    <strong style={{ color: '#004666' }}>
      {(benefitSum + (quote.financialProduct?.offers?.[0].calculation?.spreadLeasePrice?.grossAmount || 0)).toFixed(2) +
        ' ' +
        currencySymbol}
      {t('comparisonPage:perMonth')}
    </strong>
  );
};

const extractBenefitInKind = (quote: QuoteSchema.Schemas.QuoteResponse, t) => {
  const components = quote.financialProduct?.offers?.[0].benefitPackages?.[0].components;

  const componentWithFuel = components?.filter((component) => component.milesComponentTypeId === '250004')[0];
  const componentWithoutFuel = components?.filter((component) => component.milesComponentTypeId === '250003')[0];

  if (!componentWithFuel || !componentWithoutFuel) return t('comparisonPage:notApplicable');

  return (
    <>
      {`${t('comparisonPage:withFuel')}: ${componentWithFuel.alternativePeriodAmount}`}
      <br />
      {`${t('comparisonPage:withoutFuel')}: ${componentWithoutFuel.alternativePeriodAmount}`}
    </>
  );
};

export const DashboardCompare: FunctionComponent = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [quotes, setQuotes] = useState<QuoteSchema.Schemas.QuoteResponse[]>([]);
  const navigate = useNavigate();
  const offerIds = searchParams.get('offerIds');
  const selectedComparisonOfferIds = useSelector(dashboardSelectors.selectSelectedComparisonOfferIds);

  const goBackHandler = useCallback(() => {
    navigate.dashboardQuotes();
  }, [navigate]);

  const modifyRow = async (offerId: string) => {
    try {
      store.dispatch(loadingActions.setLoading(true));
      const quoteData = await getQuote(offerId);
      store.dispatch(setIsCarUsed(isCarUsedFromQuote(quoteData)));
      const customerInitialState = await getInitialCustomerStateFromQuote(quoteData);
      store.dispatch(setCustomerInitialState(customerInitialState));
      store.dispatch(setSelectedVehicleType(getVehicleType(quoteData)));
      store.dispatch(setQuoteData(quoteData));
      store.dispatch(setQuoteFlow(QuoteFlow.MODIFY));
      store.dispatch(loadingActions.setLoading(false));
      navigate.form(`/3`);
    } catch {
      store.dispatch(loadingActions.setLoading(false));
      navigate.error();
    }
  };

  const renderHeader = () => {
    return (
      <>
        <Table.Tr>
          <Table.Th />
          {quotes.map(({ financialProduct }) => {
            const { offerReference, productDescription, offerId } = financialProduct?.offers?.[0] || {};
            return (
              <Table.Th key={offerReference}>
                <span>
                  {offerReference} - {productDescription}
                </span>
                <br />
                <Button icon="semantic-edit" link simple small onClick={() => modifyRow(offerId!)}>
                  {t('comparisonPage:buttons:editButton')}
                </Button>
              </Table.Th>
            );
          })}
          {quotes.length < 3 && (
            <Table.Th>
              <Button link simple small onClick={goBackHandler}>
                {t('comparisonPage:buttons:addOffer')}
              </Button>
            </Table.Th>
          )}
        </Table.Tr>
      </>
    );
  };

  useEffect(() => {
    const ids = offerIds?.split(',') || [];
    const fetchQuotes = async () => {
      dispatch(loadingActions.setLoading(true));
      try {
        const results = await Promise.all(ids.map((id) => getQuote(id)));
        setQuotes(results);
        dispatch(loadingActions.setLoading(false));
      } catch {
        dispatch(loadingActions.setLoading(false));
      }
    };
    fetchQuotes();
  }, [dispatch, offerIds]);

  if (quotes.length === 0) return null;

  const onRemoveQuote = (offerId: string = '') => {
    setQuotes((quotes) => quotes.filter((quote) => quote.financialProduct?.offers?.[0].offerId !== offerId));
    dispatch(setSelectedComparisonOfferIds(selectedComparisonOfferIds.filter((id) => id !== offerId)));
  };

  return (
    <>
      <ContentSection pageWrap>
        <Heading level={2}>{t('comparisonPage:title')}</Heading>
        <Paragraph>{t('comparisonPage:infoText')}</Paragraph>
        <Layout center>
          <Layout.Item default="1/1" m="1/1">
            <Paragraph
              dangerouslySetInnerHTML={{ __html: t('comparisonPage:legalPlaceholder') }}
              style={{ color: 'red' }}
            />
          </Layout.Item>
        </Layout>

        <Layout className="u-mb">
          <Layout.Item>
            {quotes.length > 1 &&
              quotes.map((quote) => (
                <Tag
                  key={extractOfferId(quote)}
                  className="u-mr"
                  close
                  onCloseClick={() => onRemoveQuote(extractOfferId(quote))}
                >
                  {extractOfferReference(quote)}
                </Tag>
              ))}
          </Layout.Item>
        </Layout>

        <Table
          fixedColumns="medium"
          responsiveRowHeader
          stickyColumn
          stickyHead
          tableHeader={[
            null,
            null,
            'Marque',
            'Model',
            'Mileage',
            'Color',
            'ULEZ',
            null,
            'Body Type',
            'Year',
            'Mileage',
            'Color',
            'ULEZ',
            null,
            'Body Type',
            'Year',
            'Mileage',
            'Color',
            'ULEZ',
            null,
          ]}
        >
          <Table.Thead verticalAlign="middle">{renderHeader()}</Table.Thead>
          <Table.Tbody>
            <Table.Tr>
              <Table.Th dangerouslySetInnerHTML={{ __html: t('comparisonPage:table:brand') }}></Table.Th>
              {quotes.map((quote) => (
                <Table.Td key={extractOfferId(quote)}>
                  <strong>{quote.vehicleData?.model?.manufacturer}</strong>
                </Table.Td>
              ))}
              {quotes.length < 3 ? <Table.Td /> : <></>}
            </Table.Tr>
            <Table.Tr>
              <Table.Th headers="cat-1" id="row-1-2">
                {t('comparisonPage:table:model')}
              </Table.Th>
              {quotes.map((quote) => (
                <Table.Td key={extractOfferId(quote)}>{quote.vehicleData?.model?.name}</Table.Td>
              ))}
              {quotes.length < 3 ? <Table.Td /> : <></>}
            </Table.Tr>
            <Table.Tr>
              <Table.Th headers="cat-1" id="row-1-4">
                {t('comparisonPage:table:description')}
              </Table.Th>
              {quotes.map((quote) => (
                <Table.Td key={extractOfferId(quote)}>{quote.vehicleData?.model?.description}</Table.Td>
              ))}
              {quotes.length < 3 ? <Table.Td /> : <></>}
            </Table.Tr>
            <Table.Tr>
              <Table.Th headers="cat-1" id="row-1-4">
                {t('comparisonPage:table:energyType')}
              </Table.Th>
              {quotes.map((quote) => (
                <Table.Td key={extractOfferId(quote)}>{quote.vehicleData?.model?.engine?.fuelTypeLabel}</Table.Td>
              ))}
              {quotes.length < 3 ? <Table.Td /> : <></>}
            </Table.Tr>
            <Table.Tr>
              <Table.Th dangerouslySetInnerHTML={{ __html: t('comparisonPage:table:catalogPrice') }} />
              {quotes.map((quote) => (
                <Table.Td key={extractOfferId(quote)}>
                  {quote.vehicleData?.retailPriceAmount?.netAmount} {currencySymbol}
                </Table.Td>
              ))}
              {quotes.length < 3 ? <Table.Td /> : <></>}
            </Table.Tr>
            <Table.Tr>
              <Table.Th dangerouslySetInnerHTML={{ __html: t('comparisonPage:table:totalPrice') }} />
              {quotes.map((quote) => (
                <Table.Td key={extractOfferId(quote)}>
                  {quote.vehicleData?.retailPriceAmount?.grossAmount} {currencySymbol}
                </Table.Td>
              ))}
              {quotes.length < 3 ? <Table.Td /> : <></>}
            </Table.Tr>

            <Table.Tr>
              <Table.Th headers="cat-2" id="row-2-1">
                {t('comparisonPage:table:duration')}
              </Table.Th>
              {quotes.map((quote) => (
                <Table.Td key={extractOfferId(quote)}>
                  {quote.financialProduct?.offers?.[0].calculation?.duration} {t('comparisonPage:mois')}
                </Table.Td>
              ))}
              {quotes.length < 3 ? <Table.Td /> : <></>}
            </Table.Tr>
            <Table.Tr>
              <Table.Th headers="cat-2" id="row-2-2">
                {t('comparisonPage:table:totalMileage')}
              </Table.Th>
              {quotes.map((quote) => (
                <Table.Td key={extractOfferId(quote)}>
                  {quote.financialProduct?.offers?.[0].calculation?.contractMileage}{' '}
                  {t('comparisonPage:kilometersShortand')}
                </Table.Td>
              ))}
              {quotes.length < 3 ? <Table.Td /> : <></>}
            </Table.Tr>
            <Table.Tr>
              <Table.Th dangerouslySetInnerHTML={{ __html: t('comparisonPage:table:firstIncreasedPayment') }} />
              {quotes.map((quote) => (
                <Table.Td key={extractOfferId(quote)}>{extractFirstIncreasedPayment(quote)}</Table.Td>
              ))}
              {quotes.length < 3 ? <Table.Td /> : <></>}
            </Table.Tr>
            <Table.Tr>
              <Table.Th headers="cat-2" id="row-2-4">
                {t('comparisonPage:table:includedServices')}
              </Table.Th>
              {quotes.map((quote) => (
                <Table.Td key={extractOfferId(quote)} verticalAlign="top">
                  {extractAdditionalProducts(quote)}
                </Table.Td>
              ))}
              {quotes.length < 3 ? <Table.Td /> : <></>}
            </Table.Tr>

            <Table.Tr>
              <Table.Th dangerouslySetInnerHTML={{ __html: t('comparisonPage:table:monthlyPayment') }} />
              {quotes.map((quote) => (
                <Table.Td key={extractOfferId(quote)}>
                  <strong>
                    {`${quote.financialProduct?.offers?.[0].calculation?.spreadLeasePrice?.grossAmount} ${currencySymbol}${t('comparisonPage:perMonth')}`}
                  </strong>
                </Table.Td>
              ))}
              {quotes.length < 3 ? <Table.Td /> : <></>}
            </Table.Tr>
            <Table.Tr>
              <Table.Th headers="cat-3" id="row-3-2">
                {t('comparisonPage:table:annualEcologicalTaxes')}
              </Table.Th>
              {quotes.map((quote) => (
                <Table.Td key={extractOfferId(quote)}>{extractEcologicalTaxes(quote, t)}</Table.Td>
              ))}
              {quotes.length < 3 ? <Table.Td /> : <></>}
            </Table.Tr>
            <Table.Tr>
              <Table.Th headers="cat-3" id="row-3-3">
                {t('comparisonPage:table:nonDeductibleAnnualDepreciation')}
              </Table.Th>
              {quotes.map((quote) => (
                <Table.Td key={extractOfferId(quote)}>{extractNonDeductibleAnnualDepreciation(quote, t)}</Table.Td>
              ))}
              {quotes.length < 3 ? <Table.Td /> : <></>}
            </Table.Tr>
            <Table.Tr>
              <Table.Th
                headers="cat-3"
                id="row-3-4"
                dangerouslySetInnerHTML={{ __html: t('comparisonPage:table:totalMonthlyCost') }}
              />
              {quotes.map((quote) => (
                <Table.Td key={extractOfferId(quote)}>{extractTotalMonthlyCost(quote, t)}</Table.Td>
              ))}
              {quotes.length < 3 ? <Table.Td /> : <></>}
            </Table.Tr>
            <Table.Tr>
              <Table.Th headers="cat-3" id="row-3-4">
                {t('comparisonPage:table:benfitInKind')}
              </Table.Th>
              {quotes.map((quote) => (
                <Table.Td key={extractOfferId(quote)}>{extractBenefitInKind(quote, t)}</Table.Td>
              ))}
              {quotes.length < 3 ? <Table.Td /> : <></>}
            </Table.Tr>
          </Table.Tbody>
        </Table>
      </ContentSection>
      <ContentSection>
        <ButtonContainer center>
          <Button type="button" full onClick={goBackHandler}>
            {t('comparisonPage:buttons:backButton')}
          </Button>
        </ButtonContainer>
      </ContentSection>
    </>
  );
};
